import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const EDGE_WIDTH = 20; // px from left edge to start tracking
const SWIPE_THRESHOLD = 80; // px to complete navigation
const ROOT_ROUTES = ['/weather', '/', '/events', '/live/leaderboard', '/fantasy-golf', '/fantasy-leaderboard', '/quotas', '/gallery', '/suggestions', '/rules'];

/**
 * Enables swipe-from-left-edge to navigate back.
 * Shows a visual chevron indicator while swiping.
 * Disabled on root/tab routes and when modals are open.
 */
export function useSwipeBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const startX = useRef(0);
  const startY = useRef(0);
  const tracking = useRef(false);
  const decided = useRef(false); // whether we've decided this is a horizontal swipe

  const isDisabled = useCallback(() => {
    // Disabled on root routes
    if (ROOT_ROUTES.includes(location.pathname)) return true;
    // Disabled when modal is open
    const modalRoot = document.getElementById('modal-root');
    if (modalRoot && modalRoot.children.length > 0) return true;
    return false;
  }, [location.pathname]);

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (isDisabled()) return;
      const touch = e.touches[0];
      if (touch.clientX > EDGE_WIDTH) return;

      startX.current = touch.clientX;
      startY.current = touch.clientY;
      tracking.current = true;
      decided.current = false;
    };

    const handleTouchMove = (e) => {
      if (!tracking.current) return;

      const touch = e.touches[0];
      const dx = touch.clientX - startX.current;
      const dy = touch.clientY - startY.current;

      // Decide direction on first significant movement
      if (!decided.current) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        // If more vertical than horizontal, abort
        if (Math.abs(dy) > Math.abs(dx)) {
          tracking.current = false;
          return;
        }
        decided.current = true;
      }

      if (dx <= 0) {
        setSwipeX(0);
        setIsSwiping(false);
        return;
      }

      e.preventDefault();
      setSwipeX(Math.min(dx, 150));
      setIsSwiping(true);
    };

    const handleTouchEnd = () => {
      if (!tracking.current) return;
      tracking.current = false;

      if (swipeX >= SWIPE_THRESHOLD) {
        navigate(-1);
      }

      setSwipeX(0);
      setIsSwiping(false);
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDisabled, navigate, swipeX]);

  return { swipeX, isSwiping };
}
